// Capability matrices duplicated for the SPA so we don't pull the adapters
// package into the web bundle. Source of truth is each adapter's index.ts in
// packages/adapters/src/<provider>/. Update this file when matrices change
// (we'll automate this in Phase 7+).

import type { TtsProvider } from '../api.js';

export type CapabilityLevel = 'full' | 'partial' | 'none';

type CueHint =
  | 'hush' | 'warm_lift' | 'dry_aside' | 'concerned' | 'tender'
  | 'amused' | 'grave' | 'resolute' | 'wry' | 'urgent';

type PauseIntent = 'micro' | 'soft_trail' | 'clean_break' | 'full_beat' | 'hold_for_impact';
type EmphasisWeight = 'light' | 'strong';
type NonLexicalKind = 'breath_in' | 'breath_out' | 'sigh' | 'chuckle' | 'hum';

interface ProviderCaps {
  pause: Record<PauseIntent, CapabilityLevel>;
  cue: Record<CueHint, CapabilityLevel>;
  emphasis: Record<EmphasisWeight, CapabilityLevel>;
  non_lexical: Record<NonLexicalKind, CapabilityLevel>;
}

const fullCues: Record<CueHint, CapabilityLevel> = {
  hush: 'full', warm_lift: 'full', dry_aside: 'full', concerned: 'full',
  tender: 'full', amused: 'full', grave: 'full', resolute: 'full',
  wry: 'full', urgent: 'full',
};

const partialCues: Record<CueHint, CapabilityLevel> = {
  hush: 'partial', warm_lift: 'partial', dry_aside: 'partial', concerned: 'partial',
  tender: 'partial', amused: 'partial', grave: 'partial', resolute: 'partial',
  wry: 'partial', urgent: 'partial',
};

const MATRIX: Record<TtsProvider, ProviderCaps> = {
  openai: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: fullCues,
    emphasis: { light: 'full', strong: 'full' },
    non_lexical: { breath_in: 'partial', breath_out: 'partial', sigh: 'partial', chuckle: 'partial', hum: 'partial' },
  },
  gemini: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: fullCues,
    emphasis: { light: 'none', strong: 'none' },
    non_lexical: { breath_in: 'partial', breath_out: 'partial', sigh: 'partial', chuckle: 'partial', hum: 'partial' },
  },
  kokoro: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'none', strong: 'none' },
    non_lexical: { breath_in: 'none', breath_out: 'none', sigh: 'none', chuckle: 'none', hum: 'none' },
  },
  elevenlabs: {
    pause: { micro: 'full', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: partialCues,
    emphasis: { light: 'partial', strong: 'partial' },
    non_lexical: { breath_in: 'full', breath_out: 'full', sigh: 'full', chuckle: 'full', hum: 'partial' },
  },
  azure: {
    pause: { micro: 'full', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: partialCues,
    emphasis: { light: 'full', strong: 'full' },
    non_lexical: { breath_in: 'full', breath_out: 'full', sigh: 'partial', chuckle: 'none', hum: 'none' },
  },
  playht: {
    pause: { micro: 'full', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: partialCues,
    emphasis: { light: 'full', strong: 'full' },
    non_lexical: { breath_in: 'partial', breath_out: 'partial', sigh: 'partial', chuckle: 'none', hum: 'none' },
  },
  cartesia: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: { hush: 'full', warm_lift: 'full', dry_aside: 'partial', concerned: 'full', tender: 'full', amused: 'full', grave: 'full', resolute: 'partial', wry: 'partial', urgent: 'full' },
    emphasis: { light: 'partial', strong: 'partial' },
    non_lexical: { breath_in: 'full', breath_out: 'full', sigh: 'full', chuckle: 'full', hum: 'partial' },
  },
  deepgram: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'partial', strong: 'partial' },
    non_lexical: { breath_in: 'none', breath_out: 'none', sigh: 'none', chuckle: 'none', hum: 'none' },
  },
  fish: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'full' },
    cue: fullCues,
    emphasis: { light: 'full', strong: 'full' },
    non_lexical: { breath_in: 'partial', breath_out: 'partial', sigh: 'full', chuckle: 'full', hum: 'partial' },
  },
  chatterbox: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'none', strong: 'none' },
    non_lexical: { breath_in: 'none', breath_out: 'none', sigh: 'none', chuckle: 'none', hum: 'none' },
  },
  orpheus: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'none', strong: 'none' },
    non_lexical: { breath_in: 'full', breath_out: 'partial', sigh: 'full', chuckle: 'full', hum: 'none' },
  },
  qwen_tts: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'partial', strong: 'partial' },
    non_lexical: { breath_in: 'none', breath_out: 'none', sigh: 'none', chuckle: 'none', hum: 'none' },
  },
  dia: {
    pause: { micro: 'partial', soft_trail: 'full', clean_break: 'full', full_beat: 'full', hold_for_impact: 'partial' },
    cue: partialCues,
    emphasis: { light: 'none', strong: 'none' },
    non_lexical: { breath_in: 'partial', breath_out: 'partial', sigh: 'full', chuckle: 'full', hum: 'partial' },
  },
};

export function capabilityFor(
  spanKind: 'pause' | 'cue' | 'emphasis' | 'non_lexical',
  variant: string,
  provider: TtsProvider,
): CapabilityLevel {
  const caps = MATRIX[provider];
  // narrow safely
  const map = caps[spanKind] as Record<string, CapabilityLevel>;
  return map[variant] ?? 'partial';
}
